import { ScreenComponent } from '@app/components/common'
import { FC, useEffect } from 'react'
import { useSnapshot } from 'valtio'
import { Helmet } from 'react-helmet'
import { QuestionContextMenuProvider } from '@app/components/common/QuestionContextMenuContext'
import {
  CurrentQtiPackageState,
  CurrentTestState,
  CurrentUserAssessmentState,
  resetCurrentQtiPackage,
  resetCurrentTestState,
  resetIdleDetectorState,
  resetSyncIntervalState,
  resetTimerState,
  resetUserAssessmentState,
} from '@app/storage'
import { IconButton, styled, Tooltip } from '@mui/material'
import { AssessmentTest } from '@app/models'
import { AssessmentComponent, LeaveTestModal, Loader } from '@app/components'
import { announce } from '@app/components/common/ScreenReaderAnnouncer'
import { HOME_PAGE_BTN_ATTRIBUTES } from '@app/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { useSearchParams } from 'react-router-dom'
import {
  useUserAssessentPauseCallback,
  useUserAssessentUpdater,
  useUserAssessmentFetch,
} from '@app/helpers/hooks/userAssessment'
import {
  resetCalculatorData,
  useCurrentAssessmentSetup,
  useFeatureFlagContext,
  useIdleDetection,
  useModal,
  useTestCallback,
  useUserAssessmentIdSetup,
  getTerm,
} from '@app/helpers'
import AccessibilityTestMenuProvider from '@app/components/ui/containers/AccessibilityTestMenuContainer/AccessibilityTestMenuContext'

const brandName = getTerm('MasteryPrep')

interface TestScreenProps {}

const TestScreen: FC<TestScreenProps> = () => {
  const { currentTest, currentItemResponse } = useSnapshot(CurrentTestState)
  const { items, tests } = useSnapshot(CurrentQtiPackageState)
  const { program, subject } = useSnapshot(CurrentUserAssessmentState)
  const { featureFlagsLoading } = useFeatureFlagContext()
  const pauseUserAssessment = useUserAssessentPauseCallback()
  const handleTestCallback = useTestCallback()
  const [searchParams] = useSearchParams()

  const isPreviewMode = /^true$/i.test(searchParams.get('preview') || '')
  const leaveLabel = isPreviewMode ? 'Leave Preview' : `Leave ${getTerm('Test', true)}`
  const loadingLabel = `Loading ${getTerm('Test', true)}`

  useUserAssessmentIdSetup()

  const { userAssessment, loading: fetchLoading } = useUserAssessmentFetch()

  const {
    open: openTestLeaveModal,
    onClose: onCloseTestLeaveModal,
    onOpen: onOpenTestLeaveModal,
  } = useModal()

  const packageLoading = useCurrentAssessmentSetup(!userAssessment)
  const loading = fetchLoading || packageLoading || featureFlagsLoading

  useUserAssessentUpdater()
  useIdleDetection()

  useEffect(() => {
    // a11y: announce the boot on entry after the full-page hand-off from Ready/WorkKeys (SC 4.1.3)
    // via the persistent app-root live region. The outer gate is loading on first render, but guard
    // on it so a warm-cache instant resume can't announce with nothing loading. The visible Loader
    // label carries the same status for low-vision users.
    if (loading) announce(loadingLabel)
  }, [loading, loadingLabel])

  useEffect(() => {
    return () => {
      resetSyncIntervalState()
      resetIdleDetectorState()
      resetTimerState()
      resetCurrentTestState()
      resetCurrentQtiPackage()
      resetUserAssessmentState()
    }
  }, [])

  const onBack = async () => {
    resetCalculatorData()
    await pauseUserAssessment()
    await handleTestCallback()
  }

  const handleLeaveAssessment = () => {
    if (CurrentTestState.currentTest?.isFinished) {
      void handleTestCallback()
      return
    }

    onOpenTestLeaveModal()
  }

  const icon = (
    <Tooltip arrow title={leaveLabel ?? "Leave"}>
      <StyleIconButton
        role='button'
        aria-label={leaveLabel ?? "Leave"}
        onClick={handleLeaveAssessment}
        {...HOME_PAGE_BTN_ATTRIBUTES}
      >
        <StyledArrowLeft icon={faArrowLeft} />
      </StyleIconButton>
    </Tooltip>
  )

  return (
    <AccessibilityTestMenuProvider>
      <Helmet>
        <title>
          {[currentTest?.title, subject, program?.toUpperCase(), brandName]
            .filter(value => value)
            .join(' - ')}
        </title>
      </Helmet>

      <QuestionContextMenuProvider itemId={currentItemResponse?.itemId || 'test-screen'}>
        <ScreenComponent headerIcon={icon} test={currentTest as AssessmentTest} previewMode={isPreviewMode}>
          {loading ? (
            <Loader label={loadingLabel} />
          ) : (
            <>
              {currentTest && items.length > 0 && tests.length > 0 && (
                <AssessmentComponent userAssessment={userAssessment} />
              )}
            </>
          )}

          {currentTest && (
            <LeaveTestModal open={openTestLeaveModal} onClose={onCloseTestLeaveModal} onSubmit={onBack} />
          )}
        </ScreenComponent>
      </QuestionContextMenuProvider>
    </AccessibilityTestMenuProvider>
  )
}

export default TestScreen

const StyledArrowLeft = styled(FontAwesomeIcon)(
  ({ theme }) => `
    font-size: 1.125rem;
    color: ${theme.palette.grey[100]};
  `,
)

const StyleIconButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(12.5),
  height: theme.spacing(12.5),

  [theme.breakpoints.down('mobile')]: {
    width: theme.spacing(10),
    height: theme.spacing(10),
  },
}))
